import Types from "Types";
import ErreursConstante from "../../../_constantes/erreursConstantes.json";
import Libelles from "../../../_i18n/fr.json";
import {
  capitalizeFirstLetter,
  formatDateToJanuary,
  formatterEspaces,
} from "../../../helpers/formatters";
import {
  isAgeLocataireCorrect,
  isAlphaAndSpace,
  isAlphaNumericHyphenAndUnderscore,
  isEmail,
  isTelephoneFixe,
  isTelephonePortable,
} from "../../../helpers/validators";
import {
  CodeTypeVoie,
  DossierLocationCompositionLocationEnum,
  DossierParticulier,
  DossierParticulierDetail,
  DossierParticulierQualiteEnum,
  DossierParticulierSituationMaritaleEnum,
  DossierParticulierStatutOccupationEnum,
  DossierParticulierTypeCompteEnum,
} from "../../../services/Api/swagger";

export interface SituationPersonnelleForm {
  reference?: string;
  refDossierLocataire?: string;
  qualite?: string;
  nom?: string;
  nomDeNaissance?: string;
  prenom?: string;
  dateNaissance?: string;
  email?: string;
  telephoneFixe?: string;
  telephonePortable?: string;
  dateEntree?: string;
  statutOccupation?: string;
  numero?: string;
  numeroBatiment?: string;
  typeVoie?: string;
  adresse?: string;
  codePostalVille?: any;
  ville?: string;
  pays?: string;
  situationMaritale?: "CELIBATAIRE" | "EN_COUPLE" | "DIVORCE" | "VEUF";
  typeCompte: "LOCATAIRE" | "GARANT";
  enfants?: Array<{ dateNaissance?: string }>;
  principal?: boolean;
  qualiteConjoint?: string;
  nomConjoint?: string;
  nomDeNaissanceConjoint?: string;
  prenomConjoint?: string;
  dateNaissanceConjoint?: string;
  referenceConjoint?: string;
  compositionLocation?: DossierLocationCompositionLocationEnum;
}

export interface ISituationPersonnelleError {
  reference?: string;
  compositionLocation?: string;
  refDossierLocataire?: string;
  qualite?: string;
  nom?: string;
  nomDeNaissance?: string;
  prenom?: string;
  dateNaissance?: string;
  email?: string;
  telephoneFixe?: string;
  telephonePortable?: string;
  dateEntree?: string;
  statutOccupation?: string;
  numero?: string;
  numeroBatiment?: string;
  typeVoie?: string;
  adresse?: string;
  codePostalVille?: any;
  ville?: string;
  pays?: string;
  situationMaritale?: string;
  typeCompte: string;
  enfants?: string[];
  qualiteConjoint?: string;
  nomConjoint?: string;
  nomDeNaissanceConjoint?: string;
  prenomConjoint?: string;
  dateNaissanceConjoint?: string;
  referenceConjoint?: string;
}

export const situationMaritale = [
  {
    key: "CELIBATAIRE",
    value: "CELIBATAIRE",
    text: "Célibataire",
  },
  {
    key: "EN_COUPLE",
    value: "EN_COUPLE",
    text: "En couple",
  },
  {
    key: "DIVORCE",
    value: "DIVORCE",
    text: "Divorcé(e)/En cours de séparation",
  },
  {
    key: "VEUF",
    value: "VEUF",
    text: "Veuf(ve)",
  },
];

export const statutOccupation = [
  {
    key: "LOCATAIRE",
    value: "LOCATAIRE",
    text: "Locataire",
  },
  {
    key: "OCCUPANT_A_TITRE_GRATUIT",
    value: "OCCUPANT_A_TITRE_GRATUIT",
    text: "Occupant à titre gratuit",
  },
  {
    key: "PROPRIETAIRE",
    value: "PROPRIETAIRE",
    text: "Propriétaire",
  },
];

export const qualites = [
  {
    key: "Mme",
    value: "MME",
    text: "Mme",
  },
  {
    key: "M",
    value: "M",
    text: "M",
  },
];

// Données a renvoyer au back
export const getSituationPersonnelleModel = (
  situationPersonnelleForm: SituationPersonnelleForm,
  refDossierLocation?: string
): Types.SituationPersonnelleData => {
  const situationPersonnelle = {} as Types.SituationPersonnelleData;
  const {
    qualiteConjoint,
    nomConjoint,
    nomDeNaissanceConjoint,
    prenomConjoint,
    dateNaissanceConjoint,
    referenceConjoint,
    ...principal
  } = situationPersonnelleForm;
  situationPersonnelle.situationPrincipale = buildOneSituationPersoApi(
    principal,
    refDossierLocation
  );
  const getComposition = principal.compositionLocation;
  if (getComposition === "COUPLE" && principal.typeCompte !== "GARANT") {
    situationPersonnelle.situationConjoint = {
      qualite: qualiteConjoint,
      nom: nomConjoint && nomConjoint.toUpperCase(),
      nomDeNaissance:
        nomDeNaissanceConjoint && nomDeNaissanceConjoint.toUpperCase(),
      prenom: prenomConjoint && capitalizeFirstLetter(prenomConjoint),
      dateNaissance: dateNaissanceConjoint,
      reference: referenceConjoint,
      principal: false,
      refDossierLocation,
    } as DossierParticulier;
  }

  return situationPersonnelle;
};

// Données modifiées venant du back, pour réafficher le formulaire
export const buildSituationPersoForm = (
  data: Types.SituationPersonnelleData
): SituationPersonnelleForm => {
  let form = {} as SituationPersonnelleForm;

  const { situationPrincipale, situationConjoint } = data;

  // on enleve les elements non utilisés
  const { enfants, codePostal, ville, ...sp } = situationPrincipale;

  const codePostaVille = codePostal &&
    ville && {
      value: codePostal + "-" + ville,
      label: ville + "(" + codePostal + ")",
    };

  form = {
    ...sp,
    codePostalVille: codePostaVille,
    enfants:
      enfants && enfants.length
        ? enfants.map((s) => ({ dateNaissance: s.dateNaissance }))
        : undefined,
  };

  if (situationConjoint) {
    form = {
      ...form,
      qualiteConjoint: situationConjoint.qualite,
      nomConjoint: situationConjoint.nom,
      nomDeNaissanceConjoint: situationConjoint.nomDeNaissance,
      prenomConjoint: situationConjoint.prenom,
      dateNaissanceConjoint: situationConjoint.dateNaissance,
      referenceConjoint: situationConjoint.reference,
    };
  }

  return form;
};

// export const copySituationPersonnelleTypeToSituationPersonnelle = (
//   situationPersonnelle: Types.SituationPersonnelleDossierParticulierType
// ): SituationPersonnelleDossierParticulier => {
//   let dossierParticulier = {} as SituationPersonnelleDossierParticulier;

//   dossierParticulier = {
//     ...situationPersonnelle,
//     nom: situationPersonnelle.nom && situationPersonnelle.nom.toUpperCase(),
//     nomDeNaissance:
//       situationPersonnelle.nomDeNaissance &&
//       situationPersonnelle.nomDeNaissance.toUpperCase(),
//     prenom:
//       situationPersonnelle.prenom &&
//       capitalizeFirstLetter(situationPersonnelle.prenom),
//     qualite: situationPersonnelle.qualite
//       ? ((situationPersonnelle.qualite + "") as DossierParticulierQualiteEnum)
//       : undefined,
//     situationMaritale: situationPersonnelle.situationMaritale
//       ? ((situationPersonnelle.situationMaritale +
//           "") as DossierParticulierSituationMaritaleEnum)
//       : undefined,
//     statutOccupation: situationPersonnelle.statutOccupation
//       ? ((situationPersonnelle.statutOccupation +
//           "") as DossierParticulierStatutOccupationEnum)
//       : undefined,
//   };
//   return dossierParticulier;
// };

export const buildOneSituationPersoApi = (
  dossierParticulier: SituationPersonnelleForm,
  refDossierLocation?: string
): DossierParticulier => {
  // const {
  //   codeStatutProfessionnel,
  //   dateDebutSituationProfessionnelle,
  //   dateFinSituationProfessionnelle,
  //   pasAvisImposition,
  //   nombreMoisTravailles,
  //   salaireNetImposableM1,
  //   salaireNetImposableM2,
  //   salaireNetImposableM3,
  //   cumulSalaireNetImposableM1,
  //   cumulSalaireNetImposableM2,
  //   cumulSalaireNetImposableM3,
  //   revenuFiscalReference,
  //   dateDernierBulletin,
  //   codeApe,
  //   siret,
  //   ...sp
  // } = dossierParticulier;

  return {
    ...dossierParticulier,
    nom: dossierParticulier.nom && dossierParticulier.nom.toUpperCase(),
    nomDeNaissance:
      dossierParticulier.nomDeNaissance &&
      dossierParticulier.nomDeNaissance.toUpperCase(),
    prenom:
      dossierParticulier.prenom &&
      capitalizeFirstLetter(dossierParticulier.prenom),
    codePostal: dossierParticulier.codePostalVille.value.split("-")[0],
    ville: dossierParticulier.codePostalVille.value.split("-")[1],
    refDossierLocation,
    telephoneFixe:
      dossierParticulier.telephoneFixe &&
      formatterEspaces(dossierParticulier.telephoneFixe),
    telephonePortable:
      dossierParticulier.telephonePortable &&
      formatterEspaces(dossierParticulier.telephonePortable),
    typeCompte:
      dossierParticulier.typeCompte as DossierParticulierTypeCompteEnum,
    typeVoie: dossierParticulier.typeVoie as CodeTypeVoie,
    qualite: dossierParticulier.qualite
      ? DossierParticulierQualiteEnum[
          dossierParticulier.qualite as DossierParticulierQualiteEnum
        ]
      : undefined,
    situationMaritale: dossierParticulier.situationMaritale
      ? ((dossierParticulier.situationMaritale +
          "") as DossierParticulierSituationMaritaleEnum)
      : undefined,
    statutOccupation: dossierParticulier.statutOccupation
      ? ((dossierParticulier.statutOccupation +
          "") as DossierParticulierStatutOccupationEnum)
      : undefined,
    enfants:
      dossierParticulier.enfants &&
      dossierParticulier.enfants.map((enfant) => {
        const date = formatDateToJanuary(enfant.dateNaissance);
        return {
          ...enfant,
          dateNaissance: date,
        };
      }),
  };
};

export const buildSituationFormOnModification = (
  dossierParticulier: DossierParticulierDetail,
  situationPersoConjoint?: DossierParticulierDetail
) => {
  const {
    codeStatutProfessionnel,
    dateDebutSituationProfessionnelle,
    dateFinSituationProfessionnelle,
    pasAvisImposition,
    nombreMoisTravailles,
    salaireNetImposableM1,
    salaireNetImposableM2,
    salaireNetImposableM3,
    cumulSalaireNetImposableM1,
    cumulSalaireNetImposableM2,
    cumulSalaireNetImposableM3,
    revenuFiscalReference,
    dateDernierBulletin,
    revenuIndividuelDeclare,
    codeApe,
    siret,
    dossierPieces,
    ...sp
  } = dossierParticulier;

  let form = {} as SituationPersonnelleForm;

  form = {
    ...sp,
    codePostalVille: dossierParticulier.codePostal &&
      dossierParticulier.ville && {
        value: dossierParticulier.codePostal + "-" + dossierParticulier.ville,
        label:
          dossierParticulier.ville + "(" + dossierParticulier.codePostal + ")",
      },
    enfants:
      dossierParticulier.enfants && dossierParticulier.enfants.length > 0
        ? dossierParticulier.enfants.map((s) => ({
            dateNaissance: s.dateNaissance,
          }))
        : undefined,
  };

  if (situationPersoConjoint) {
    form = {
      ...form,
      qualiteConjoint: situationPersoConjoint.qualite,
      dateNaissanceConjoint: situationPersoConjoint.dateNaissance,
      nomConjoint: situationPersoConjoint.nom,
      nomDeNaissanceConjoint: situationPersoConjoint.nomDeNaissance,
      prenomConjoint: situationPersoConjoint.prenom,
      referenceConjoint: situationPersoConjoint.reference,
    };
  }
  return form;
};

/**
 * Méthode de validation du formulaire situation personnelle
 * @param values
 */
export const validateForm = (
  values: SituationPersonnelleForm,
  hasConjoint?: boolean
): ISituationPersonnelleError => {
  const errors: ISituationPersonnelleError = {} as ISituationPersonnelleError;

  if (!values?.qualite) {
    errors.qualite = ErreursConstante.formulaire.generale.champObligatoire;
  }

  if (values && !values.compositionLocation && values.principal) {
    errors.compositionLocation =
      ErreursConstante.formulaire.generale.champObligatoire;
  }
  if (values && !values.nom) {
    errors.nom = ErreursConstante.formulaire.generale.champObligatoire;
  } else if (values.nom && !isAlphaAndSpace(values.nom)) {
    errors.nom = ErreursConstante.formulaire.generale.formatAlphaAndSpace;
  }

  if (values.nomDeNaissance && !isAlphaAndSpace(values.nomDeNaissance)) {
    errors.nomDeNaissance =
      ErreursConstante.formulaire.generale.formatAlphaAndSpace;
  }

  if (!values?.prenom) {
    errors.prenom = ErreursConstante.formulaire.generale.champObligatoire;
  } else if (!isAlphaAndSpace(values.prenom)) {
    errors.prenom = ErreursConstante.formulaire.generale.formatAlphaAndSpace;
  }

  if (!values?.dateNaissance) {
    errors.dateNaissance =
      ErreursConstante.formulaire.generale.champObligatoire;
  } else if (
    values &&
    values.dateNaissance &&
    !isAgeLocataireCorrect(values.dateNaissance)
  ) {
    errors.dateNaissance =
      ErreursConstante.formulaire.generale.verifierDateNaissance;
  }

  if (!values?.email) {
    errors.email = ErreursConstante.formulaire.generale.champObligatoire;
  } else if (values.email && !isEmail(values.email)) {
    errors.email = ErreursConstante.formulaire.generale.emailIncorrect;
  }

  if (values.telephoneFixe && !isTelephoneFixe(values.telephoneFixe)) {
    errors.telephoneFixe =
      ErreursConstante.formulaire.generale.formatTelephoneFixeIncorrect;
  }

  if (!values?.telephonePortable) {
    errors.telephonePortable =
      ErreursConstante.formulaire.generale.champObligatoire;
  } else if (
    values &&
    values.telephonePortable &&
    !isTelephonePortable(values.telephonePortable)
  ) {
    errors.telephonePortable =
      ErreursConstante.formulaire.generale.formatTelephonePortableIncorrect;
  }

  if (!values?.statutOccupation) {
    errors.statutOccupation =
      ErreursConstante.formulaire.generale.champObligatoire;
  }

  if (values.numero && !isAlphaNumericHyphenAndUnderscore(values.numero)) {
    errors.numero = ErreursConstante.formulaire.generale.formatAlphaNumeric;
  }

  if (
    values &&
    values.numeroBatiment &&
    !isAlphaNumericHyphenAndUnderscore(values.numeroBatiment)
  ) {
    errors.numeroBatiment =
      ErreursConstante.formulaire.generale.formatAlphaNumeric;
  }

  if (!values?.typeVoie) {
    errors.typeVoie = ErreursConstante.formulaire.generale.champObligatoire;
  }

  if (!values?.adresse) {
    errors.adresse = ErreursConstante.formulaire.generale.champObligatoire;
  } else if (
    !isAlphaNumericHyphenAndUnderscore(values.adresse.replace(/\s/g, ""))
  ) {
    errors.adresse = ErreursConstante.formulaire.generale.formatAlphaNumeric;
  }

  // TODO Why ce champ ?
  // if (!values?.ville) {
  //   errors.ville = ErreursConstante.formulaire.generale.champObligatoire;
  // }

  if (!values?.codePostalVille) {
    errors.codePostalVille =
      ErreursConstante.formulaire.generale.champObligatoire;
  }

  if (!values?.situationMaritale) {
    errors.situationMaritale =
      ErreursConstante.formulaire.generale.champObligatoire;
  }

  if (!values?.nomConjoint && hasConjoint) {
    errors.nomConjoint = ErreursConstante.formulaire.generale.champObligatoire;
  }

  if (!values?.prenomConjoint && hasConjoint) {
    errors.prenomConjoint =
      ErreursConstante.formulaire.generale.champObligatoire;
  }

  if (!values?.dateNaissanceConjoint && hasConjoint) {
    errors.dateNaissanceConjoint =
      ErreursConstante.formulaire.generale.champObligatoire;
  } else if (
    values &&
    hasConjoint &&
    values.dateNaissanceConjoint &&
    !isAgeLocataireCorrect(values.dateNaissanceConjoint)
  ) {
    errors.dateNaissanceConjoint =
      ErreursConstante.formulaire.generale.verifierDateNaissance;
  }

  if (!values?.qualiteConjoint && hasConjoint) {
    errors.qualiteConjoint =
      ErreursConstante.formulaire.generale.champObligatoire;
  }

  if (values.enfants && values.enfants.length > 0) {
    const enfantsErrors = [] as string[];
    values.enfants.map((enfant, index) => {
      const enfantError = {} as any;
      if (!enfant.dateNaissance) {
        enfantError.dateNaissance =
          ErreursConstante.formulaire.generale.champObligatoire;
        enfantsErrors[index] = enfantError;
      } else {
        // if (!isAgeEnfantCorrect(enfant.dateNaissance)) {
        //   enfantError.dateNaissance =
        //     ErreursConstante.formulaire.generale.verifierDateNaissance;
        //   enfantsErrors[index] = enfantError;
        // }
      }
    });
    if (enfantsErrors.length) {
      errors.enfants = enfantsErrors;
    }
  }
  return errors;
};

// @params DossierLocationCompositionLocationEnum
export const buildCompositionLocation = () => {
  const values = Object.keys(
    DossierLocationCompositionLocationEnum
  ) as string[];
  return values.map((v: string) => ({
    value: v,
    label: Libelles.typeDossier[v as keyof typeof Libelles.typeDossier],
  }));
};
